import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate } from "react-router-dom";
import { 
  Users, Plus, X, AlertTriangle, Shield, Star, Coins, 
  ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, 
  Trophy, ChevronsUp, Image as ImageIcon, Sparkles, Check, 
  ExternalLink, Globe, Gamepad2, Swords, MessageSquare, Radio, Palette
} from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";

const BANNER_PRESETS = [
  { id: "b1", label: "Tactical Red", url: "https://images.unsplash.com/photo-1542751371-adc38448a05e?auto=format&fit=crop&w=800" },
  { id: "b2", label: "Cyber City", url: "https://images.unsplash.com/photo-1538481199705-c710c4e965fc?auto=format&fit=crop&w=800" },
  { id: "b3", label: "Inferno War", url: "https://images.unsplash.com/photo-1614680376573-df3480f0c6ff?auto=format&fit=crop&w=800" },
  { id: "b4", label: "Neon Apex", url: "https://images.unsplash.com/photo-1551103782-8ab07afd45c1?auto=format&fit=crop&w=800" },
  { id: "b5", label: "Dark Matrix", url: "https://images.unsplash.com/photo-1526374965328-7f61d4dc18c5?auto=format&fit=crop&w=800" },
  { id: "b6", label: "Arctic Strike", url: "https://images.unsplash.com/photo-1547036967-23d11aacaee0?auto=format&fit=crop&w=800" },
];

const LOGO_PRESETS = [
  { id: "l1", label: "Dragon Skull", url: "https://api.dicebear.com/7.x/identicon/svg?seed=DragonSkull&backgroundColor=b6e3f4,c0aede,d1d4f9,ffd5dc,ffdfbf" },
  { id: "l2", label: "Cyber Wolf", url: "https://api.dicebear.com/7.x/bottts/svg?seed=CyberWolf&backgroundColor=ff4f00" },
  { id: "l3", label: "War Titan", url: "https://api.dicebear.com/7.x/identicon/svg?seed=WarTitan&backgroundColor=00e5ff" },
  { id: "l4", label: "Shadow Ops", url: "https://api.dicebear.com/7.x/bottts/svg?seed=ShadowOps&backgroundColor=7928ca" },
  { id: "l5", label: "Phoenix Fire", url: "https://api.dicebear.com/7.x/identicon/svg?seed=PhoenixFire&backgroundColor=f59e0b" },
  { id: "l6", label: "Viper Elite", url: "https://api.dicebear.com/7.x/bottts/svg?seed=ViperElite&backgroundColor=10b981" },
];

const COLOR_PRESETS = [
  { id: "orange", hex: "#ff4f00", label: "Tangerine" },
  { id: "cyan", hex: "#00e5ff", label: "Cyan" },
  { id: "red", hex: "#ef4444", label: "Crimson" },
  { id: "purple", hex: "#a855f7", label: "Purple" },
  { id: "amber", hex: "#eab308", label: "Gold" },
  { id: "emerald", hex: "#10b981", label: "Emerald" },
];

export default function Clans() {
  const [page, setPage] = useState(1);
  const limit = 12;
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [activeModalTab, setActiveModalTab] = useState("info"); // 'info' | 'visuals' | 'details'
  
  const [newClan, setNewClan] = useState({
    name: "",
    tag: "",
    motto: "",
    description: "",
    game_type: "cs16",
    logo_url: "",
    banner_url: BANNER_PRESETS[0].url,
    accent_color: "#ff4f00",
    discord: "",
    steam: "",
    is_recruiting: true,
    is_private: false
  });
  
  const { user } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { data, isLoading, error } = useQuery({
    queryKey: ["clans", page],
    queryFn: async () => {
      const skip = (page - 1) * limit;
      const res = await api.get(`/clans?skip=${skip}&limit=${limit}`);
      return res.data?.clans || [];
    }
  });

  const createMutation = useMutation({
    mutationFn: async (clanData) => {
      const res = await api.post("/clans", clanData);
      return res.data;
    },
    onSuccess: (data) => {
      toast.success("Clan creat cu succes!");
      queryClient.invalidateQueries(["clans"]);
      setShowCreateModal(false);
      navigate(`/clans/${data.id || data._id}`);
    },
    onError: (err) => {
      toast.error(formatApiError(err) || "Eroare la crearea clanului");
    }
  });

  const handleCreateSubmit = (e) => {
    e.preventDefault();
    if (!newClan.name.trim()) {
      toast.error("Numele clanului este obligatoriu!");
      return;
    }
    if (!newClan.tag.trim()) {
      toast.error("Tag-ul clanului este obligatoriu!");
      return;
    }

    const payload = {
      name: newClan.name.trim(),
      tag: newClan.tag.trim().toUpperCase(),
      motto: newClan.motto.trim(),
      description: newClan.description.trim(),
      game_type: newClan.game_type,
      logo_url: newClan.logo_url.trim() || `https://ui-avatars.com/api/?background=${newClan.accent_color.replace('#','')}&color=000&bold=true&name=${newClan.tag}`,
      banner_url: newClan.banner_url.trim() || BANNER_PRESETS[0].url,
      accent_color: newClan.accent_color,
      discord: newClan.discord.trim(),
      steam: newClan.steam.trim(),
      is_recruiting: newClan.is_recruiting
    };

    createMutation.mutate(payload);
  };

  const [searchQuery, setSearchQuery] = useState("");
  const [filterGame, setFilterGame] = useState("all");
  const [sortBy, setSortBy] = useState("rank");

  const clans = (data || []).filter(c => {
    if (filterGame !== "all" && c.game_type !== filterGame) return false;
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      const matchName = (c.name || "").toLowerCase().includes(q);
      const matchTag = (c.tag || "").toLowerCase().includes(q);
      if (!matchName && !matchTag) return false;
    }
    return true;
  }).sort((a, b) => {
    if (sortBy === "members") return (b.member_count || 1) - (a.member_count || 1);
    if (sortBy === "level") return (b.level || 1) - (a.level || 1);
    return (a.rank || 1) - (b.rank || 1);
  });

  return (
    <div className="max-w-[1500px] mx-auto space-y-6">
      {/* 1. Hero Command Header */}
      <div className="bg-[#0b0e15] border border-white/10 p-4 sm:p-6 flex flex-col md:flex-row md:items-center justify-between gap-4 relative overflow-hidden shadow-2xl">
        <div className="absolute top-0 right-0 w-80 h-full bg-gradient-to-l from-[var(--tf-primary)]/10 via-transparent to-transparent pointer-events-none" />
        <div className="flex items-center gap-3 relative z-10">
          <div 
            className="w-12 h-12 flex items-center justify-center text-black font-black shrink-0 shadow-lg"
            style={{ backgroundColor: "var(--tf-primary)" }}
          >
            <Shield className="w-6 h-6" />
          </div>
          <div>
            <h1 className="font-chakra text-2xl sm:text-3xl font-black uppercase tracking-wider text-white">
              ECHIPE & CLANURI
            </h1>
            <p className="text-xs text-zinc-400 font-mono">
              // Formează o echipă, recrutează membri și domină clasamentul competițional INDUNGI
            </p>
          </div>
        </div>

        {user && (
          <div className="flex items-center gap-3 relative z-10">
            <button 
              className="tf-btn-primary tf-clip-btn flex items-center gap-2 px-5 py-2.5 text-xs font-black uppercase tracking-wider shadow-lg"
              onClick={() => {
                setNewClan({
                  name: "",
                  tag: "",
                  motto: "",
                  description: "",
                  game_type: "cs16",
                  logo_url: "",
                  banner_url: BANNER_PRESETS[0].url,
                  accent_color: "#ff4f00",
                  discord: "",
                  steam: "",
                  is_recruiting: true
                });
                setActiveModalTab("info");
                setShowCreateModal(true);
              }}
            >
              <Plus className="w-4 h-4" />
              <span>CREEAZĂ O ECHIPĂ PRO</span>
            </button>
          </div>
        )}
      </div>

      {/* 2. Tactical Search & Filter Bar */}
      <div className="bg-[#0b0e15] border border-white/10 p-3 flex flex-col sm:flex-row items-center justify-between gap-3">
        <div className="relative flex-1 w-full">
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Caută după nume de clan sau tag..."
            className="w-full bg-[#07090e] border border-white/10 px-4 py-2 text-xs font-mono text-white focus:outline-none focus:border-[var(--tf-primary)]"
          />
        </div>

        <div className="flex items-center gap-2 w-full sm:w-auto">
          <select
            value={filterGame}
            onChange={(e) => setFilterGame(e.target.value)}
            className="bg-[#07090e] border border-white/10 px-3 py-2 text-xs font-mono text-white focus:outline-none focus:border-[var(--tf-primary)] cursor-pointer"
          >
            <option value="all">Toate Jocurile</option>
            <option value="cs16">Counter-Strike 1.6</option>
            <option value="cs2">Counter-Strike 2</option>
            <option value="mix">Mix Competitiv</option>
          </select>

          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            className="bg-[#07090e] border border-white/10 px-3 py-2 text-xs font-mono text-white focus:outline-none focus:border-[var(--tf-primary)] cursor-pointer"
          >
            <option value="rank">Top Rang</option>
            <option value="members">Cei mai mulți membri</option>
            <option value="level">Nivel Clan</option>
          </select>
        </div>
      </div>

      {isLoading ? (
        <div className="clan-cards clan-cards__inline-4 animate-pulse">
          {[1,2,3,4,5,6,7,8].map(i => (
            <div key={i} className="bg-[#0c0f16] border border-white/10 rounded-xl h-64 skeleton--default" />
          ))}
        </div>
      ) : error ? (
        <div className="text-center py-16 bg-[#0b0e15] border border-white/10 rounded-xl">
          <AlertTriangle className="w-12 h-12 text-red-500 mx-auto mb-3 opacity-60" />
          <h2 className="text-lg font-black font-chakra text-white mb-1">Nu s-au putut încărca clanurile</h2>
          <p className="text-zinc-500 text-xs font-mono">{formatApiError(error)}</p>
        </div>
      ) : clans.length === 0 ? (
        <div className="text-center py-16 bg-[#0b0e15] border border-white/10 rounded-xl">
          <Shield className="w-12 h-12 text-zinc-600 mx-auto mb-3 opacity-40" />
          <h2 className="text-lg font-black font-chakra text-white mb-1">Nu a fost găsit niciun clan</h2>
          <p className="text-zinc-500 text-xs font-mono">Fii primul care își formează o echipă!</p>
        </div>
      ) : (
        <>
          <div className="clan-cards clan-cards__inline-4">
            {clans.map((clan, idx) => {
              const position = (page - 1) * limit + idx + 1;
              const rating = clan.points || clan.rating || (10000 - position * 450);
              const bank = clan.bank || clan.coins || (50000 - position * 2100);
              const memberCount = clan.member_count || clan.members?.length || 1;
              const maxSlots = clan.max_members || clan.slots || (clan.level ? clan.level * 20 : 10);
              const levelText = clan.level >= 5 ? "Legend" : `Nivel ${clan.level || 1}`;
              const joinStatus = clan.is_private ? "Privat" : clan.is_recruiting !== false ? "Pe Cerere" : "Închis";

              return (
                <Link 
                  key={clan.id || clan._id} 
                  to={`/clans/${clan.id || clan._id}`}
                  className="clan-card"
                  data-id={clan.id || clan._id}
                >
                  <div className="clan-card__position">#{position}</div>
                  
                  <div className="clan-card__media">
                    <img 
                      className="clan-card__banner" 
                      src={clan.banner_url || "https://images.unsplash.com/photo-1542751371-adc38448a05e?auto=format&fit=crop&w=800"} 
                      alt={`Banner ${clan.name}`} 
                      loading="lazy" 
                      decoding="async"
                    />
                    <img 
                      className="clan-card__avatar" 
                      src={clan.logo_url || `https://ui-avatars.com/api/?background=${(clan.accent_color || '#ff4f00').replace('#','')}&color=000&bold=true&name=${clan.tag || 'CLAN'}`} 
                      alt={`Avatar ${clan.name}`} 
                      loading="lazy" 
                      decoding="async"
                    />
                  </div>

                  <div className="clan-card__content">
                    <div className="clan-card__head">
                      <h3 className="clan-card__title" title={clan.name}>{clan.name}</h3>
                      <span className="clan-card__tag">{clan.tag}</span>
                    </div>

                    <div className="clan-card__stats">
                      {/* Rating Stat */}
                      <div className="clan-card__stat clan-card__stat--rating" title="Rating Competitiv">
                        <svg viewBox="0 0 640 640">
                          <path d="M341.5 45.1C337.4 37.1 329.1 32 320.1 32C311.1 32 302.8 37.1 298.7 45.1L225.1 189.3L65.2 214.7C56.3 216.1 48.9 222.4 46.1 231C43.3 239.6 45.6 249 51.9 255.4L166.3 369.9L141.1 529.8C139.7 538.7 143.4 547.7 150.7 553C158 558.3 167.6 559.1 175.7 555L320.1 481.6L464.4 555C472.4 559.1 482.1 558.3 489.4 553C496.7 547.7 500.4 538.8 499 529.8L473.7 369.9L588.1 255.4C594.5 249 596.7 239.6 593.9 231C591.1 222.4 583.8 216.1 574.8 214.7L415 189.3L341.5 45.1z"/>
                        </svg>
                        <span>{rating > 0 ? rating.toLocaleString() : "1,000"}</span>
                      </div>

                      {/* Bank Stat */}
                      <div className="clan-card__stat clan-card__stat--bank" title="Tezaur / Bancă Clan">
                        <svg viewBox="0 0 16 16">
                          <path fill="currentColor" d="M9.368 1.222a1 1 0 0 0-1.414.15L5.058 5h1.28l2.397-3.004l.77.629L7.575 5h1.288l1.417-1.744l1.718 1.403L11.7 5h.3a3 3 0 0 1 .88.131a1 1 0 0 0-.25-1.245zM3 5.5a.5.5 0 0 1 .5-.5h.558l.795-1H3.5A1.5 1.5 0 0 0 2 5.5v6A2.5 2.5 0 0 0 4.5 14H12a2 2 0 0 0 2-2V8a2 2 0 0 0-2-2H3.5a.5.5 0 0 1-.5-.5m7.5 4.5h1a.5.5 0 0 1 0 1h-1a.5.5 0 0 1 0-1"/>
                        </svg>
                        <span>{bank > 0 ? bank.toLocaleString() : "0"}</span>
                      </div>

                      {/* Players / Slots Stat */}
                      <div className="clan-card__stat clan-card__stat--players" title="Membri Activi / Sloturi">
                        <svg viewBox="0 0 640 640">
                          <path d="M320 80C377.4 80 424 126.6 424 184C424 241.4 377.4 288 320 288C262.6 288 216 241.4 216 184C216 126.6 262.6 80 320 80zM96 152C135.8 152 168 184.2 168 224C168 263.8 135.8 296 96 296C56.2 296 24 263.8 24 224C24 184.2 56.2 152 96 152zM0 480C0 409.3 57.3 352 128 352C140.8 352 153.2 353.9 164.9 357.4C132 394.2 112 442.8 112 496L112 512C112 523.4 114.4 534.2 118.7 544L32 544C14.3 544 0 529.7 0 512L0 480zM521.3 544C525.6 534.2 528 523.4 528 512L528 496C528 442.8 508 394.2 475.1 357.4C486.8 353.9 499.2 352 512 352C582.7 352 640 409.3 640 480L640 512C640 529.7 625.7 544 608 544L521.3 544zM472 224C472 184.2 504.2 152 544 152C583.8 152 616 184.2 616 224C616 263.8 583.8 296 544 296C504.2 296 472 263.8 472 224zM160 496C160 407.6 231.6 336 320 336C408.4 336 480 407.6 480 496L480 512C480 529.7 465.7 544 448 544L192 544C174.3 544 160 529.7 160 512L160 496z"/>
                        </svg>
                        <span>{memberCount}/{maxSlots}</span>
                      </div>
                    </div>

                    <div className="clan-card__footer">
                      <span>{levelText}</span>
                      <span className="clan-card__dot"></span>
                      <span>{joinStatus}</span>
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>

          {/* GameCMS Neo Remastered Pagination */}
          <div className="pagination">
            <button 
              disabled={page === 1}
              onClick={() => setPage(1)}
              className={`button_pagination ${page === 1 ? 'disabled' : ''}`}
              title="Prima pagină"
            >
              <ChevronsLeft className="w-4 h-4" />
            </button>
            <button 
              disabled={page === 1}
              onClick={() => setPage(p => Math.max(1, p - 1))}
              className={`button_pagination ${page === 1 ? 'disabled' : ''}`}
              title="Pagina precedentă"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            
            <span className="button_pagination active">
              {page}
            </span>
            
            <button 
              disabled={clans.length < limit}
              onClick={() => setPage(p => p + 1)}
              className={`button_pagination ${clans.length < limit ? 'disabled' : ''}`}
              title="Pagina următoare"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
            <button 
              disabled={clans.length < limit}
              onClick={() => setPage(p => p + 1)}
              className={`button_pagination ${clans.length < limit ? 'disabled' : ''}`}
              title="Ultima pagină"
            >
              <ChevronsRight className="w-4 h-4" />
            </button>
          </div>
        </>
      )}

      {/* PRO CREATE CLAN MODAL */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/85 backdrop-blur-md z-50 flex items-center justify-center p-4 overflow-y-auto">
          <div className="bg-[#0b0e15] border border-white/10 w-full max-w-2xl overflow-hidden relative shadow-2xl my-8">
            <div 
              className="h-[3px] w-full"
              style={{ backgroundColor: newClan.accent_color || "var(--tf-primary)" }}
            />
            
            <div className="p-6">
              {/* Modal Header */}
              <div className="flex justify-between items-center mb-4 pb-3 border-b border-white/10">
                <div className="flex items-center gap-3">
                  <div 
                    className="w-9 h-9 flex items-center justify-center text-black font-black"
                    style={{ backgroundColor: newClan.accent_color || "var(--tf-primary)" }}
                  >
                    <Shield className="w-5 h-5" />
                  </div>
                  <div>
                    <h2 className="text-lg font-black font-chakra text-white uppercase tracking-wider">
                      Creează Clan / Divizie PRO
                    </h2>
                    <p className="text-[11px] text-zinc-400 font-mono">// Configurează identitatea, grafica și detaliile de recrutare</p>
                  </div>
                </div>
                <button 
                  onClick={() => setShowCreateModal(false)} 
                  className="text-zinc-500 hover:text-white p-1"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* LIVE CLAN CARD PREVIEW */}
              <div className="mb-5 bg-[#07090e] border border-white/10 overflow-hidden relative">
                {/* Banner Preview */}
                <div className="h-24 w-full relative bg-zinc-950 overflow-hidden">
                  <img 
                    src={newClan.banner_url || BANNER_PRESETS[0].url} 
                    alt="Banner Preview" 
                    className="w-full h-full object-cover opacity-50"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#07090e] via-[#07090e]/40 to-transparent" />
                  
                  {/* Game Mode Pill */}
                  <div className="absolute top-2 right-2 flex items-center gap-1.5 z-10">
                    <span 
                      className="px-2 py-0.5 text-[9px] font-black font-chakra uppercase text-black"
                      style={{ backgroundColor: newClan.accent_color || "var(--tf-primary)" }}
                    >
                      {newClan.game_type?.toUpperCase()}
                    </span>
                    {newClan.is_recruiting && (
                      <span className="px-2 py-0.5 text-[9px] font-bold font-mono uppercase bg-emerald-500/20 text-emerald-400 border border-emerald-500/40">
                        ● Recrutare Deschisă
                      </span>
                    )}
                  </div>
                </div>

                {/* Body Details Preview */}
                <div className="p-3.5 pt-0 flex items-start gap-3 relative -mt-8 z-10">
                  <div 
                    className="w-14 h-14 border-2 bg-black overflow-hidden shrink-0 shadow-xl"
                    style={{ borderColor: newClan.accent_color || "var(--tf-primary)" }}
                  >
                    <img 
                      src={newClan.logo_url || `https://ui-avatars.com/api/?background=${(newClan.accent_color || '#ff4f00').replace('#','')}&color=000&bold=true&name=${newClan.tag || 'TAG'}`} 
                      alt="Logo" 
                      className="w-full h-full object-cover"
                    />
                  </div>

                  <div className="min-w-0 flex-1 pt-8">
                    <div className="flex items-center gap-2">
                      <span 
                        className="text-xs font-black font-chakra px-1.5 py-0.5 text-black uppercase"
                        style={{ backgroundColor: newClan.accent_color || "var(--tf-primary)" }}
                      >
                        [{newClan.tag || "TAG"}]
                      </span>
                      <span className="text-sm font-black text-white font-chakra truncate">
                        {newClan.name || "Nume Clan"}
                      </span>
                    </div>
                    <p className="text-[11px] text-zinc-400 font-mono italic truncate mt-0.5">
                      "{newClan.motto || "Motto-ul echipei tale..."}"
                    </p>
                  </div>
                </div>
              </div>

              {/* MODAL NAVIGATION TABS */}
              <div className="flex items-center gap-2 border-b border-white/10 mb-4 pb-2">
                <button
                  type="button"
                  onClick={() => setActiveModalTab("info")}
                  className={`px-3 py-1.5 text-xs font-chakra font-black uppercase tracking-wider transition-all border ${
                    activeModalTab === "info"
                      ? "bg-[var(--tf-primary)] text-black border-[var(--tf-primary)]"
                      : "bg-white/5 border-white/10 text-zinc-400 hover:text-white"
                  }`}
                >
                  1. Informații Generale
                </button>
                <button
                  type="button"
                  onClick={() => setActiveModalTab("visuals")}
                  className={`px-3 py-1.5 text-xs font-chakra font-black uppercase tracking-wider transition-all border ${
                    activeModalTab === "visuals"
                      ? "bg-[var(--tf-primary)] text-black border-[var(--tf-primary)]"
                      : "bg-white/5 border-white/10 text-zinc-400 hover:text-white"
                  }`}
                >
                  2. Avatar & Banner
                </button>
                <button
                  type="button"
                  onClick={() => setActiveModalTab("details")}
                  className={`px-3 py-1.5 text-xs font-chakra font-black uppercase tracking-wider transition-all border ${
                    activeModalTab === "details"
                      ? "bg-[var(--tf-primary)] text-black border-[var(--tf-primary)]"
                      : "bg-white/5 border-white/10 text-zinc-400 hover:text-white"
                  }`}
                >
                  3. Recrutare & Social
                </button>
              </div>

              <form onSubmit={handleCreateSubmit} className="space-y-4">
                {/* TAB 1: INFO */}
                {activeModalTab === "info" && (
                  <div className="space-y-3.5">
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                      <div className="sm:col-span-2">
                        <label className="block text-xs font-bold text-zinc-300 mb-1 font-chakra uppercase tracking-wider">
                          Nume Clan <span className="text-[var(--tf-primary)]">*</span>
                        </label>
                        <input 
                          type="text" 
                          required
                          className="w-full bg-[#07090e] border border-white/10 px-3 py-2 text-xs font-mono text-white focus:outline-none focus:border-[var(--tf-primary)]"
                          value={newClan.name} 
                          onChange={e => setNewClan({...newClan, name: e.target.value})} 
                          placeholder="ex: Avangard Gaming" 
                        />
                      </div>

                      <div>
                        <label className="block text-xs font-bold text-zinc-300 mb-1 font-chakra uppercase tracking-wider">
                          Tag Clan <span className="text-[var(--tf-primary)]">*</span> <span className="text-[10px] text-zinc-500">(max 5)</span>
                        </label>
                        <input 
                          type="text" 
                          required
                          maxLength={5}
                          className="w-full bg-[#07090e] border border-white/10 px-3 py-2 text-xs font-mono uppercase text-white focus:outline-none focus:border-[var(--tf-primary)] tracking-widest font-bold"
                          value={newClan.tag} 
                          onChange={e => setNewClan({...newClan, tag: e.target.value.toUpperCase().slice(0, 5)})} 
                          placeholder="AVG" 
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-xs font-bold text-zinc-300 mb-1 font-chakra uppercase tracking-wider">
                        Slogan / Motto
                      </label>
                      <input 
                        type="text" 
                        className="w-full bg-[#07090e] border border-white/10 px-3 py-2 text-xs font-mono text-white focus:outline-none focus:border-[var(--tf-primary)]"
                        value={newClan.motto} 
                        onChange={e => setNewClan({...newClan, motto: e.target.value})} 
                        placeholder="ex: Play to dominate, stay legendary." 
                      />
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs font-bold text-zinc-300 mb-1 font-chakra uppercase tracking-wider">
                          Mod Principal de Joc
                        </label>
                        <select
                          className="w-full bg-[#07090e] border border-white/10 px-3 py-2 text-xs font-mono text-white focus:outline-none focus:border-[var(--tf-primary)] cursor-pointer"
                          value={newClan.game_type}
                          onChange={e => setNewClan({...newClan, game_type: e.target.value})}
                        >
                          <option value="cs16">Counter-Strike 1.6</option>
                          <option value="cs2">Counter-Strike 2</option>
                          <option value="mix">Mix Competitiv 5v5</option>
                          <option value="all">Multi-Gaming</option>
                        </select>
                      </div>

                      <div>
                        <label className="block text-xs font-bold text-zinc-300 mb-1 font-chakra uppercase tracking-wider">
                          Culoare Accent Clan
                        </label>
                        <div className="flex items-center gap-1.5 pt-1">
                          {COLOR_PRESETS.map(c => (
                            <button
                              key={c.id}
                              type="button"
                              onClick={() => setNewClan({...newClan, accent_color: c.hex})}
                              className={`w-7 h-7 rounded border-2 transition-all flex items-center justify-center ${
                                newClan.accent_color === c.hex ? 'scale-110 border-white shadow-lg' : 'border-transparent opacity-70 hover:opacity-100'
                              }`}
                              style={{ backgroundColor: c.hex }}
                              title={c.label}
                            >
                              {newClan.accent_color === c.hex && <Check className="w-3.5 h-3.5 text-black font-black" />}
                            </button>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* TAB 2: VISUALS */}
                {activeModalTab === "visuals" && (
                  <div className="space-y-4">
                    {/* Logo Presets */}
                    <div>
                      <label className="block text-xs font-bold text-zinc-300 mb-1.5 font-chakra uppercase tracking-wider">
                        Alege Logo / Avatar sau Introdu URL
                      </label>
                      <div className="grid grid-cols-6 gap-2 mb-2">
                        {LOGO_PRESETS.map(lp => (
                          <button
                            key={lp.id}
                            type="button"
                            onClick={() => setNewClan({...newClan, logo_url: lp.url})}
                            className={`aspect-square bg-black border p-1 transition-all overflow-hidden relative ${
                              newClan.logo_url === lp.url ? 'border-[var(--tf-primary)] scale-105' : 'border-white/10 hover:border-white/30'
                            }`}
                          >
                            <img src={lp.url} alt={lp.label} className="w-full h-full object-contain" />
                          </button>
                        ))}
                      </div>
                      <input 
                        type="url" 
                        className="w-full bg-[#07090e] border border-white/10 px-3 py-2 text-xs font-mono text-white focus:outline-none focus:border-[var(--tf-primary)]"
                        value={newClan.logo_url} 
                        onChange={e => setNewClan({...newClan, logo_url: e.target.value})} 
                        placeholder="Sau lipește un link direct de imagine (https://...)" 
                      />
                    </div>

                    {/* Banner Presets */}
                    <div>
                      <label className="block text-xs font-bold text-zinc-300 mb-1.5 font-chakra uppercase tracking-wider">
                        Alege Banner Copertă sau Introdu URL
                      </label>
                      <div className="grid grid-cols-3 gap-2 mb-2">
                        {BANNER_PRESETS.map(bp => (
                          <button
                            key={bp.id}
                            type="button"
                            onClick={() => setNewClan({...newClan, banner_url: bp.url})}
                            className={`h-12 bg-black border relative overflow-hidden transition-all ${
                              newClan.banner_url === bp.url ? 'border-[var(--tf-primary)] scale-105' : 'border-white/10 hover:border-white/30'
                            }`}
                          >
                            <img src={bp.url} alt={bp.label} className="w-full h-full object-cover opacity-60" />
                            <span className="absolute bottom-1 left-1 bg-black/80 px-1 py-0.5 text-[8px] font-mono text-white">
                              {bp.label}
                            </span>
                          </button>
                        ))}
                      </div>
                      <input 
                        type="url" 
                        className="w-full bg-[#07090e] border border-white/10 px-3 py-2 text-xs font-mono text-white focus:outline-none focus:border-[var(--tf-primary)]"
                        value={newClan.banner_url} 
                        onChange={e => setNewClan({...newClan, banner_url: e.target.value})} 
                        placeholder="Sau lipește un link direct de banner (https://...)" 
                      />
                    </div>
                  </div>
                )}

                {/* TAB 3: DETAILS & RECRUITING */}
                {activeModalTab === "details" && (
                  <div className="space-y-3.5">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs font-bold text-zinc-300 mb-1 font-chakra uppercase tracking-wider">
                          Server Discord
                        </label>
                        <input 
                          type="text" 
                          className="w-full bg-[#07090e] border border-white/10 px-3 py-2 text-xs font-mono text-white focus:outline-none focus:border-[var(--tf-primary)]"
                          value={newClan.discord} 
                          onChange={e => setNewClan({...newClan, discord: e.target.value})} 
                          placeholder="https://discord.gg/..." 
                        />
                      </div>

                      <div>
                        <label className="block text-xs font-bold text-zinc-300 mb-1 font-chakra uppercase tracking-wider">
                          Grup Steam
                        </label>
                        <input 
                          type="text" 
                          className="w-full bg-[#07090e] border border-white/10 px-3 py-2 text-xs font-mono text-white focus:outline-none focus:border-[var(--tf-primary)]"
                          value={newClan.steam} 
                          onChange={e => setNewClan({...newClan, steam: e.target.value})} 
                          placeholder="https://steamcommunity.com/groups/..." 
                        />
                      </div>
                    </div>

                    <div>
                      <label className="flex items-center gap-2 cursor-pointer pt-1">
                        <input 
                          type="checkbox"
                          checked={newClan.is_recruiting}
                          onChange={e => setNewClan({...newClan, is_recruiting: e.target.checked})}
                          className="w-4 h-4 accent-orange-500 rounded"
                        />
                        <span className="text-xs font-chakra font-bold text-white uppercase tracking-wider">
                          Permite aplicarea noilor membri (Recrutare Deschisă)
                        </span>
                      </label>
                    </div>

                    <div className="bg-[#0a0d14] border border-amber-500/20 p-3 mt-1">
                      <label className="flex items-center gap-3 cursor-pointer">
                        <input 
                          type="checkbox"
                          checked={newClan.is_private}
                          onChange={e => setNewClan({...newClan, is_private: e.target.checked})}
                          className="w-4 h-4 accent-amber-500 rounded"
                        />
                        <div>
                          <span className="text-xs font-chakra font-bold text-amber-400 uppercase tracking-wider flex items-center gap-1.5">
                            🔒 Clan Privat
                          </span>
                          <p className="text-[10px] text-zinc-500 font-mono mt-0.5">
                            Vizitatorii și membrii care nu fac parte din clan nu vor vedea informațiile confidențiale (participanți, activitate, anunțuri, IDG Coins).
                          </p>
                        </div>
                      </label>
                    </div>

                    <div>
                      <label className="block text-xs font-bold text-zinc-300 mb-1 font-chakra uppercase tracking-wider">
                        Descriere & Reguli Clan
                      </label>
                      <textarea 
                        className="w-full bg-[#07090e] border border-white/10 px-3 py-2 text-xs font-mono text-white focus:outline-none focus:border-[var(--tf-primary)] min-h-[75px] resize-none"
                        value={newClan.description} 
                        onChange={e => setNewClan({...newClan, description: e.target.value})} 
                        placeholder="Descrie obiectivele echipei, cerințele pentru membrii noi și stilul de joc..." 
                      />
                    </div>
                  </div>
                )}

                {/* Modal Footer Actions */}
                <div className="mt-6 pt-4 border-t border-white/10 flex items-center justify-between gap-3">
                  <button 
                    type="button"
                    className="bg-white/5 hover:bg-white/10 text-zinc-300 hover:text-white font-bold py-2.5 px-5 text-xs uppercase tracking-wider font-chakra border border-white/10 transition-colors" 
                    onClick={() => setShowCreateModal(false)}
                  >
                    Anulează
                  </button>

                  <button 
                    type="submit"
                    className="tf-btn-primary tf-clip-btn py-2.5 px-6 disabled:opacity-50 text-xs uppercase tracking-wider font-black shadow-lg flex items-center gap-2"
                    style={{ backgroundColor: newClan.accent_color || "var(--tf-primary)" }}
                    disabled={createMutation.isPending || !newClan.name.trim() || !newClan.tag.trim()}
                  >
                    <Swords className="w-4 h-4" />
                    <span>{createMutation.isPending ? "Se creează..." : "⚔️ CREEAZĂ CLANUL ACUM"}</span>
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
